import { useQuery } from '@apollo/client/react'
import { Text } from '@chakra-ui/react'
import { PaginationState, SortingState } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import { AdminPageLayout } from '../../../components/AdminPageLayout/AdminPageLayout'
import { DataTable, DataTablePagination } from '../../../components/DataTable'
import {
  AdminOrganization,
  adminOrganizationsQuery,
  mapOrganizationsSortingToOrderBy,
} from '../../../services/adminOrganizationsService'
import {
  ConnectionResult,
  getConnectionPageCount,
} from '../../../services/listQuery'
import {
  OrganizationsAdminFilters,
  useOrganizationsAdminColumns,
} from './OrganizationsAdminPage.parts'

export const OrganizationsAdminPage = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdDate', desc: true },
  ])
  const [filters, setFilters] = useState({
    name: '',
    displayName: '',
    auth0Id: '',
  })

  const filterInput = useMemo(
    () => ({
      ...(filters.name && { name: { contains: filters.name } }),
      ...(filters.displayName && {
        displayName: { contains: filters.displayName },
      }),
      ...(filters.auth0Id && { auth0Id: { contains: filters.auth0Id } }),
    }),
    [filters],
  )

  const { data, previousData, loading } = useQuery(adminOrganizationsQuery, {
    variables: {
      first: pagination.pageSize,
      skip: pagination.pageIndex * pagination.pageSize,
      filter: Object.keys(filterInput).length > 0 ? filterInput : undefined,
      orderBy: mapOrganizationsSortingToOrderBy(sorting),
    },
    notifyOnNetworkStatusChange: true,
  })

  const connection =
    (
      data as
        | { Organization_list?: ConnectionResult<AdminOrganization> }
        | undefined
    )?.Organization_list ??
    (
      previousData as
        | { Organization_list?: ConnectionResult<AdminOrganization> }
        | undefined
    )?.Organization_list ??
    undefined
  const organizations = connection?.items ?? []
  const totalCount = connection?.totalCount ?? 0
  const isLoading = loading && connection === undefined
  const pageCount = getConnectionPageCount(totalCount, pagination.pageSize)

  const columns = useOrganizationsAdminColumns()

  return (
    <AdminPageLayout pageTitle="Organizations">
      <Text mb={4} color="presets.neutral.70">
        List of all organizations in the system.
      </Text>

      <OrganizationsAdminFilters
        filters={filters}
        setFilters={setFilters}
        setPaginationPageIndex={pageIndex =>
          setPagination(current => ({ ...current, pageIndex }))
        }
      />

      <DataTable
        data={organizations}
        columns={columns}
        isLoading={isLoading}
        manualPagination
        manualSorting
        manualFiltering
        pagination={pagination}
        pageCount={pageCount}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={nextSorting => {
          setSorting(nextSorting)
          setPagination(current => ({ ...current, pageIndex: 0 }))
        }}
        height="calc(100vh - 200px)"
      />
      <DataTablePagination
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        pageCount={pageCount}
        totalCount={totalCount}
        isLoading={isLoading}
        onPageChange={pageIndex =>
          setPagination(current => ({ ...current, pageIndex }))
        }
        onPageSizeChange={pageSize =>
          setPagination({
            pageIndex: 0,
            pageSize,
          })
        }
      />
    </AdminPageLayout>
  )
}
